import React, { useState, useContext, useEffect, Fragment } from 'react';
import moment from 'moment';
import get from 'lodash/get';
import { FloatingBubble, InfiniteScroll, DotLoading, Toast, PullToRefresh, SpinLoading, ErrorBlock } from 'antd-mobile';
import { useHistory } from 'react-router-dom';
import { AddOutline } from 'antd-mobile-icons';
import styled from 'styled-components';
import { addFlightLog, getFlightLog, getList } from '../api';
import FlightLogList from '../components/LogbookList';
import { matchesPermission } from '../utils/permission-helpers';

import flightloggerLogo from '../assets/images/flightlogger_logo.png';
import leonLogo from '../assets/images/leon-logo-full.svg';
import { incrementSequenceNumber, serialize } from '../utils/helpers';
import UserSelfContext from '../utils/self-context';
import { UTILIZATION_STATUS_COMPLETED } from '../utils/constants';

const LogbookWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;

const ListWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow:  auto;
	width: 100%;
	background: #f0f2f5;
`;

const ToastContent = styled.div`
	word-break: normal;
	text-align: center;
	width: 100%;
`;

function Logbook(props) {
	const {
		aircraftId,
		flightloggerEnabled,
		leonEnabled
	} = props;
	const { userSelf } = useContext(UserSelfContext);
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [latestRecord, setLatestRecord] = useState(null);
	const [err, setErr] = useState(false);
	const [records, setRecords] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(1);

	async function fetchRecords(currentPage, silent, clear) {
		if (!silent) {
			setLoading(true);
		}

		try {
			const data = await getFlightLog(aircraftId, `?page=${currentPage}`);
			const newList = [...(clear ? [] : records), ...data.items];
			setHasMore(data.items?.length && newList.length < data.total_count);
			setRecords(newList);
			setPage(currentPage);
		} catch (e) {
			setErr(true);
		}

		setLoading(false);
	}

	async function getLatestRecord() {
		try {
			const data = await getList('tech_logs', `?${serialize({ aircraft_id: aircraftId, latest: true })}`);
			if (data && data.length > 0) {
				setLatestRecord(data[0]);
			}
		} catch (e) {
			// Ignore
		}
	}

	useEffect(() => {
		if (aircraftId) {
			fetchRecords(page);
			getLatestRecord();
		}
	}, [aircraftId]);


	async function onLoadMore() {
		const newPage = page + 1;
		await fetchRecords(newPage, true);
	}

	const InfiniteScrollContent = ({ more }) => {
		return (
			<Fragment>
				{more ? (
					<Fragment>
						<span>Loading</span>
						<DotLoading />
					</Fragment>
				) : (
					<span></span>
				)}
			</Fragment>
		);
	};

	function generateFlightNumber() {
		return incrementSequenceNumber(latestRecord ? latestRecord?.flight : '');
	}

	function generateTlog() {
		return incrementSequenceNumber(latestRecord ? latestRecord?.tlog : '');
	}

	async function addNew() {
		setSaving(true);
		try {
			const newItem = await addFlightLog(aircraftId, {
				date_toff: moment(),
				time_toff: moment().format('HH:mm:ss'),
				date_off_block: moment(),
				time_off_block: moment().format('HH:mm:ss'),
				date_on_block: moment(),
				from: get(records, '[0].to') || null,
				tlog: generateTlog(),
				status: UTILIZATION_STATUS_COMPLETED,
				flight: generateFlightNumber()
			});

			history.push(`/aircraft/${aircraftId}/tech-log/${newItem.id}`);
		} catch (e) {
			Toast.show({
				icon: 'fail',
				content: <ToastContent>
					Something went wrong, cannot create new logbook entry.
				</ToastContent>
			});
		}
		setSaving(false);
	}

	return (
		<LogbookWrapper>
			{
				matchesPermission(userSelf, 'add_utilization') &&
				!flightloggerEnabled &&
				!leonEnabled &&
				<FloatingBubble
					style={{
						'--initial-position-bottom': '74px',
						'--initial-position-right': 'calc(50% - 24px)',
						'--edge-distance': '24px'
					}}
					onClick={addNew}
				>
					{saving ? <SpinLoading /> : <AddOutline fontSize={32} />}
				</FloatingBubble>
			}
			{!flightloggerEnabled && !leonEnabled && <PullToRefresh
				onRefresh={async () => {
					await fetchRecords(1, true, true);
				}}
			>
				<ListWrapper>
					<FlightLogList
						saving={saving}
						records={records}
						loading={loading}
					/>
					{(!loading && hasMore) ? <InfiniteScroll
						loadMore={onLoadMore}
						hasMore={hasMore}
					>
						<InfiniteScrollContent />
					</InfiniteScroll> : null}
				</ListWrapper>
			</PullToRefresh>}
			{flightloggerEnabled && <ErrorBlock
				image={flightloggerLogo}
				style={{
					marginTop: 64,
					'--image-height': '40px'
				}}
				title="This aircraft has FlightLogger integration enabled"
				description="Please use FlightLogger to log all flight activities"
			/>}
			{leonEnabled && <ErrorBlock
				image={leonLogo}
				style={{
					marginTop: 64,
					'--image-height': '80px'
				}}
				title="This aircraft has Leon integration enabled"
				description="Please use Leon to log all flight activities"
			/>}
		</LogbookWrapper>
	);
}

export default Logbook;
